type MjcfFlag = "default" | "true" | "false";

export interface MjcfCompilerSettings {
  autolimits: "true" | "false";
  strippath: MjcfFlag;
  usethread: MjcfFlag;
  boundmass: number | null;
  boundinertia: number | null;
  settotalmass: number | null;
  balanceinertia: MjcfFlag;
  discardvisual: MjcfFlag;
  convexhull: MjcfFlag;
  fusestatic: MjcfFlag;
  inertiafromgeom: MjcfFlag;
  inertiagrouprange: [number | null, number | null];
  fitaabb: MjcfFlag;
}

export interface MjcfStatisticSettings {
  meanmass: number | null;
  meaninertia: number | null;
  meansize: number | null;
  extent: number | null;
  center: [number | null, number | null, number | null];
}

export const DEFAULT_COMPILER_SETTINGS: MjcfCompilerSettings = {
  autolimits: "true",
  strippath: "default",
  usethread: "default",
  boundmass: null,
  boundinertia: null,
  settotalmass: null,
  balanceinertia: "default",
  discardvisual: "default",
  convexhull: "default",
  fusestatic: "default",
  inertiafromgeom: "default",
  inertiagrouprange: [null, null],
  fitaabb: "default",
};

export const DEFAULT_STATISTIC_SETTINGS: MjcfStatisticSettings = {
  meanmass: null,
  meaninertia: null,
  meansize: null,
  extent: null,
  center: [null, null, null],
};

const finiteOrNull = (value: unknown): number | null => {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const flagOrDefault = (value: unknown): MjcfFlag =>
  value === "true" || value === "false" ? value : "default";

const autolimitsOrDefault = (value: unknown): "true" | "false" =>
  value === "false" ? "false" : "true";

export const normalizeCompilerSettings = (value: unknown): MjcfCompilerSettings => {
  const raw = value && typeof value === "object" ? (value as Record<string, unknown>) : {};
  const inertiagrouprange = Array.isArray(raw.inertiagrouprange)
    ? raw.inertiagrouprange
    : DEFAULT_COMPILER_SETTINGS.inertiagrouprange;
  return {
    autolimits: autolimitsOrDefault(raw.autolimits),
    strippath: flagOrDefault(raw.strippath),
    usethread: flagOrDefault(raw.usethread),
    boundmass: finiteOrNull(raw.boundmass),
    boundinertia: finiteOrNull(raw.boundinertia),
    settotalmass: finiteOrNull(raw.settotalmass),
    balanceinertia: flagOrDefault(raw.balanceinertia),
    discardvisual: flagOrDefault(raw.discardvisual),
    convexhull: flagOrDefault(raw.convexhull),
    fusestatic: flagOrDefault(raw.fusestatic),
    inertiafromgeom: flagOrDefault(raw.inertiafromgeom),
    inertiagrouprange: [finiteOrNull(inertiagrouprange[0]), finiteOrNull(inertiagrouprange[1])],
    fitaabb: flagOrDefault(raw.fitaabb),
  };
};

export const normalizeStatisticSettings = (value: unknown): MjcfStatisticSettings => {
  const raw = value && typeof value === "object" ? (value as Record<string, unknown>) : {};
  const center = Array.isArray(raw.center) ? raw.center : DEFAULT_STATISTIC_SETTINGS.center;
  return {
    meanmass: finiteOrNull(raw.meanmass),
    meaninertia: finiteOrNull(raw.meaninertia),
    meansize: finiteOrNull(raw.meansize),
    extent: finiteOrNull(raw.extent),
    center: [finiteOrNull(center[0]), finiteOrNull(center[1]), finiteOrNull(center[2])],
  };
};

const xmlEscapeAttr = (value: string): string =>
  value.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

const appendFiniteAttr = (attrs: string[], name: string, value: number | null) => {
  if (value === null) return;
  if (!Number.isFinite(value)) return;
  attrs.push(`${name}="${String(value)}"`);
};

const appendFlagAttr = (attrs: string[], name: string, value: MjcfFlag) => {
  if (value === "default") return;
  attrs.push(`${name}="${value}"`);
};

export const buildMjcfCompilerXml = (compiler: MjcfCompilerSettings): string => {
  const settings = normalizeCompilerSettings(compiler);
  const attrs = ['angle="radian"', 'meshdir="assets"', `autolimits="${settings.autolimits}"`];
  appendFlagAttr(attrs, "strippath", settings.strippath);
  appendFlagAttr(attrs, "usethread", settings.usethread);
  appendFiniteAttr(attrs, "boundmass", settings.boundmass);
  appendFiniteAttr(attrs, "boundinertia", settings.boundinertia);
  appendFiniteAttr(attrs, "settotalmass", settings.settotalmass);
  appendFlagAttr(attrs, "balanceinertia", settings.balanceinertia);
  appendFlagAttr(attrs, "discardvisual", settings.discardvisual);
  appendFlagAttr(attrs, "convexhull", settings.convexhull);
  appendFlagAttr(attrs, "fusestatic", settings.fusestatic);
  appendFlagAttr(attrs, "inertiafromgeom", settings.inertiafromgeom);
  if (settings.inertiagrouprange.every((value) => value !== null && Number.isFinite(value))) {
    attrs.push(
      `inertiagrouprange="${settings.inertiagrouprange.map((value) => String(Math.trunc(value ?? 0))).join(" ")}"`
    );
  }
  appendFlagAttr(attrs, "fitaabb", settings.fitaabb);
  return `<compiler ${attrs.join(" ")}/>`;
};

export const buildMjcfStatisticXml = (statistic: MjcfStatisticSettings): string => {
  const settings = normalizeStatisticSettings(statistic);
  const attrs: string[] = [];
  appendFiniteAttr(attrs, "meanmass", settings.meanmass);
  appendFiniteAttr(attrs, "meaninertia", settings.meaninertia);
  appendFiniteAttr(attrs, "meansize", settings.meansize);
  appendFiniteAttr(attrs, "extent", settings.extent);
  if (settings.center.every((value) => value !== null && Number.isFinite(value))) {
    attrs.push(
      `center="${settings.center.map((value) => xmlEscapeAttr(String(value))).join(" ")}"`
    );
  }
  return attrs.length > 0 ? `<statistic ${attrs.join(" ")}/>` : "";
};

export const parseMjcfCompilerSettings = (xmlDoc: Document): MjcfCompilerSettings => {
  const compiler = xmlDoc.querySelector("mujoco > compiler, compiler");
  if (!compiler) return DEFAULT_COMPILER_SETTINGS;
  const parseRange = (value: string | null): [number | null, number | null] => {
    const parts = String(value || "")
      .trim()
      .split(/\s+/)
      .map(Number);
    return [finiteOrNull(parts[0]), finiteOrNull(parts[1])];
  };
  return normalizeCompilerSettings({
    autolimits: compiler.getAttribute("autolimits"),
    strippath: compiler.getAttribute("strippath"),
    usethread: compiler.getAttribute("usethread"),
    boundmass: compiler.getAttribute("boundmass"),
    boundinertia: compiler.getAttribute("boundinertia"),
    settotalmass: compiler.getAttribute("settotalmass"),
    balanceinertia: compiler.getAttribute("balanceinertia"),
    discardvisual: compiler.getAttribute("discardvisual"),
    convexhull: compiler.getAttribute("convexhull"),
    fusestatic: compiler.getAttribute("fusestatic"),
    inertiafromgeom: compiler.getAttribute("inertiafromgeom"),
    inertiagrouprange: parseRange(compiler.getAttribute("inertiagrouprange")),
    fitaabb: compiler.getAttribute("fitaabb"),
  });
};

export const parseMjcfStatisticSettings = (xmlDoc: Document): MjcfStatisticSettings => {
  const statistic = xmlDoc.querySelector("mujoco > statistic, statistic");
  if (!statistic) return DEFAULT_STATISTIC_SETTINGS;
  const parseCenter = (value: string | null): [number | null, number | null, number | null] => {
    const parts = String(value || "")
      .trim()
      .split(/\s+/)
      .map(Number);
    return [finiteOrNull(parts[0]), finiteOrNull(parts[1]), finiteOrNull(parts[2])];
  };
  return normalizeStatisticSettings({
    meanmass: statistic.getAttribute("meanmass"),
    meaninertia: statistic.getAttribute("meaninertia"),
    meansize: statistic.getAttribute("meansize"),
    extent: statistic.getAttribute("extent"),
    center: parseCenter(statistic.getAttribute("center")),
  });
};
